// React
import React, { useEffect, useMemo, useRef, useState } from "react";

// External libraries
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClipboard, faClipboardCheck, faClock } from "@fortawesome/sharp-solid-svg-icons";

// Internal
import { DropdownAlignment, DropdownDirection, DropdownPanel, DropdownPosition, useDropdownPanel } from "../dropdown-panel/dropdown-panel";
import { getDataTestId } from "../functions/data-test-ids";
import { InputCopyButton } from "./input-clipboard-copy-button";
import { InputErrorIndicator } from "./input-error-indicator";
import { InputIcon } from "./input-icon";
import { getInputClassDetails } from "./input-shared.jsx";

// CSS
import "./inputs.css";

const PICKER_MAX_HEIGHT = 400;

function pad2(n) {
    return String(n).padStart(2, "0");
}

function parseTime(value) {
    if (typeof value !== "string" || value.length === 0) {
        return { hours24: null, minutes: null };
    }
    const cleaned = value.replace(/:/g, "");
    if (!/^\d{3,4}$/.test(cleaned)) {
        return { hours24: null, minutes: null };
    }
    const normalized = cleaned.padStart(4, "0");
    const hours24 = parseInt(normalized.slice(0, 2), 10);
    const minutes = parseInt(normalized.slice(2, 4), 10);
    if (hours24 > 23 || minutes > 59) {
        return { hours24: null, minutes: null };
    }
    return { hours24, minutes };
}

function hoursTo12(hours24) {
    if (hours24 == null) return null;
    return (hours24 % 12) || 12;
}

function toHours24(hours12, period) {
    if (period === "AM") {
        return hours12 === 12 ? 0 : hours12;
    }
    return hours12 === 12 ? 12 : hours12 + 12;
}

function periodFromHours24(hours24) {
    if (hours24 == null) return "AM";
    return hours24 >= 12 ? "PM" : "AM";
}

function buildOutput(hoursStr, minutesStr, period, usesAmPm) {
    const h = parseInt(hoursStr, 10);
    const m = parseInt(minutesStr, 10);
    if (isNaN(h) || isNaN(m)) return "";
    if (m < 0 || m > 59) return "";
    let hours24;
    if (usesAmPm) {
        if (h < 1 || h > 12) return "";
        hours24 = toHours24(h, period);
    } else {
        if (h < 0 || h > 23) return "";
        hours24 = h;
    }
    return `${pad2(hours24)}:${pad2(m)}`;
}

function TimePickerColumn(props) {
    const containerRef = useRef(null);
    const selectedRef = useRef(null);

    useEffect(() => {
        if (props.Open && selectedRef.current && containerRef.current) {
            const container = containerRef.current;
            const selected = selectedRef.current;
            container.scrollTop = selected.offsetTop - container.clientHeight / 2 + selected.clientHeight / 2;
        }
    }, [props.Open, props.Selected]);

    return (
        <div className="eone-ui-input-time-picker-column" ref={containerRef}>
            {props.Options.map((option) => {
                const isSelected = option.value === props.Selected;
                return (
                    <button
                        key={option.value}
                        ref={isSelected ? selectedRef : null}
                        type="button"
                        className={`eone-ui-input-time-picker-cell${isSelected ? " selected" : ""}`}
                        onClick={() => props.OnSelect(option.value)}
                        data-testid={getDataTestId(props.DataTestId, option.value)}>
                        {option.label}
                    </button>
                );
            })}
        </div>
    );
}

function InputTime(props) {
    const usesAmPm = useMemo(() => {
        const locale = props.Locale ?? (typeof navigator !== "undefined" ? navigator.language : "en-US");
        const hour12 = new Intl.DateTimeFormat(locale, { hour: "numeric" }).resolvedOptions().hour12;
        return hour12 ?? true;
    }, [props.Locale]);

    const initial = useMemo(() => {
        const parsed = parseTime(props.Value);
        if (parsed.hours24 == null) {
            return { hours: "", minutes: "", period: "AM" };
        }
        return {
            hours: pad2(usesAmPm ? hoursTo12(parsed.hours24) : parsed.hours24),
            minutes: pad2(parsed.minutes),
            period: periodFromHours24(parsed.hours24),
        };
    }, []);

    const [copied, setCopied] = useState(false);
    const [focusedSegment, setFocusedSegment] = useState(null);
    const [hoursStr, setHoursStr] = useState(initial.hours);
    const [minutesStr, setMinutesStr] = useState(initial.minutes);
    const [periodStr, setPeriodStr] = useState(initial.period);

    const hoursRef = useRef(null);
    const minutesRef = useRef(null);
    const periodRef = useRef(null);
    const containerRef = useRef(null);
    const dropdownPanelRef = useRef(null);
    const lastEmittedRef = useRef(props.Value ?? "");

    // Sync from props.Value when it changes externally (not from our own emit).
    useEffect(() => {
        const incoming = props.Value ?? "";
        if (incoming === lastEmittedRef.current) return;
        lastEmittedRef.current = incoming;
        const parsed = parseTime(incoming);
        if (parsed.hours24 == null) {
            setHoursStr("");
            setMinutesStr("");
        } else {
            setHoursStr(pad2(usesAmPm ? hoursTo12(parsed.hours24) : parsed.hours24));
            setMinutesStr(pad2(parsed.minutes));
            setPeriodStr(periodFromHours24(parsed.hours24));
        }
    }, [props.Value, usesAmPm]);

    // If locale changes (12h ↔ 24h), re-project hours display from canonical.
    useEffect(() => {
        const parsed = parseTime(props.Value);
        if (parsed.hours24 != null) {
            setHoursStr(pad2(usesAmPm ? hoursTo12(parsed.hours24) : parsed.hours24));
            setPeriodStr(periodFromHours24(parsed.hours24));
        }
    }, [usesAmPm]);

    const showPicker = props.ShowPicker !== false;
    const minuteStep = props.MinuteStep && props.MinuteStep > 0 ? props.MinuteStep : 1;

    const {
        open,
        close,
        toggleOpen,
        triggerRef,
        style,
    } = useDropdownPanel(
        DropdownPosition.Below,
        DropdownDirection.Down,
        DropdownAlignment.AlignLeftAndRight,
        1,
        PICKER_MAX_HEIGHT,
        PICKER_MAX_HEIGHT,
        null
    );

    useEffect(() => {
        if (!open) return;
        const handleClickOutside = (e) => {
            const outsideTrigger = triggerRef?.current && !triggerRef.current.contains(e.target);
            const outsidePanel = dropdownPanelRef.current && !dropdownPanelRef.current.contains(e.target);
            if (outsideTrigger && outsidePanel) {
                close();
            }
        };
        window.addEventListener("mousedown", handleClickOutside);
        return () => window.removeEventListener("mousedown", handleClickOutside);
    }, [open]);

    const emit = (h, m, p) => {
        const output = buildOutput(h, m, p, usesAmPm);
        lastEmittedRef.current = output;
        if (typeof props.OnChange === "function") {
            props.OnChange(output);
        }
        return output;
    };

    const handleHoursChange = (e) => {
        const digits = e.target.value.replace(/\D/g, "").slice(0, 2);
        setHoursStr(digits);
        emit(digits, minutesStr, periodStr);

        const numeric = parseInt(digits, 10);
        const maxSingleDigit = usesAmPm ? 1 : 2;
        if (digits.length === 2 || (digits.length === 1 && numeric > maxSingleDigit)) {
            minutesRef.current?.focus();
            minutesRef.current?.select();
        }
    };

    const handleMinutesChange = (e) => {
        const digits = e.target.value.replace(/\D/g, "").slice(0, 2);
        setMinutesStr(digits);
        emit(hoursStr, digits, periodStr);
    };

    const stepHours = (delta) => {
        const base = hoursStr === "" ? (usesAmPm ? 12 : 0) : parseInt(hoursStr, 10);
        let next;
        if (usesAmPm) {
            const cur = (base === 0 || isNaN(base)) ? 12 : base;
            next = ((cur - 1 + delta + 12) % 12) + 1;
        } else {
            const cur = isNaN(base) ? 0 : base;
            next = (cur + delta + 24) % 24;
        }
        const hours = pad2(next);
        const minutes = minutesStr === "" ? "00" : minutesStr;
        setHoursStr(hours);
        setMinutesStr(minutes);
        emit(hours, minutes, periodStr);
    };

    const stepMinutes = (delta) => {
        const base = minutesStr === "" ? 0 : parseInt(minutesStr, 10);
        const cur = isNaN(base) ? 0 : base;
        const next = (cur + delta + 60) % 60;
        const minutes = pad2(next);
        const hours = hoursStr === "" ? (usesAmPm ? "12" : "00") : hoursStr;
        setHoursStr(hours);
        setMinutesStr(minutes);
        emit(hours, minutes, periodStr);
    };

    const setPeriod = (nextPeriod) => {
        if (nextPeriod === periodStr) return;
        setPeriodStr(nextPeriod);
        const hours = hoursStr === "" ? "12" : hoursStr;
        const minutes = minutesStr === "" ? "00" : minutesStr;
        setHoursStr(hours);
        setMinutesStr(minutes);
        const output = emit(hours, minutes, nextPeriod);
        if (typeof props.OnBlur === "function") {
            props.OnBlur(output);
        }
    };

    const togglePeriod = () => {
        setPeriod(periodStr === "AM" ? "PM" : "AM");
    };

    const handleHoursKeyDown = (e) => {
        if (e.key === "ArrowUp") {
            e.preventDefault();
            stepHours(1);
        } else if (e.key === "ArrowDown") {
            e.preventDefault();
            stepHours(-1);
        } else if (e.key === "ArrowRight" && e.target.selectionStart === e.target.value.length) {
            e.preventDefault();
            minutesRef.current?.focus();
            minutesRef.current?.select();
        } else if (e.key === ":" || e.key === " ") {
            e.preventDefault();
            minutesRef.current?.focus();
            minutesRef.current?.select();
        }
    };

    const handleMinutesKeyDown = (e) => {
        if (e.key === "ArrowUp") {
            e.preventDefault();
            stepMinutes(1);
        } else if (e.key === "ArrowDown") {
            e.preventDefault();
            stepMinutes(-1);
        } else if (e.key === "ArrowLeft" && e.target.selectionStart === 0) {
            e.preventDefault();
            hoursRef.current?.focus();
            hoursRef.current?.select();
        } else if (e.key === "Backspace" && e.target.selectionStart === 0 && e.target.selectionEnd === 0) {
            e.preventDefault();
            hoursRef.current?.focus();
            hoursRef.current?.select();
        } else if (usesAmPm && (e.key === "a" || e.key === "A" || e.key === "p" || e.key === "P")) {
            e.preventDefault();
            periodRef.current?.focus();
            setPeriod((e.key === "a" || e.key === "A") ? "AM" : "PM");
        }
    };

    const handlePeriodKeyDown = (e) => {
        if (e.key === "ArrowUp" || e.key === "ArrowDown" || e.key === " " || e.key === "Enter") {
            e.preventDefault();
            togglePeriod();
        } else if (e.key === "a" || e.key === "A") {
            e.preventDefault();
            setPeriod("AM");
        } else if (e.key === "p" || e.key === "P") {
            e.preventDefault();
            setPeriod("PM");
        } else if (e.key === "ArrowLeft") {
            e.preventDefault();
            minutesRef.current?.focus();
            minutesRef.current?.select();
        }
    };

    const handlePaste = (e) => {
        const pasted = e.clipboardData?.getData?.("text");
        if (!pasted) return;
        const match = pasted.match(/(\d{1,2})\s*:?\s*(\d{2})\s*(am|pm)?/i);
        if (!match) return;
        e.preventDefault();
        const h = parseInt(match[1], 10);
        const m = parseInt(match[2], 10);
        let nextPeriod = periodStr;
        if (match[3]) {
            nextPeriod = match[3].toUpperCase();
        } else if (usesAmPm && h > 12) {
            nextPeriod = "PM";
        }
        const hours = usesAmPm ? pad2((h % 12) || 12) : pad2(h);
        const minutes = pad2(m);
        setHoursStr(hours);
        setMinutesStr(minutes);
        setPeriodStr(nextPeriod);
        emit(hours, minutes, nextPeriod);
    };

    const handleSegmentFocus = (segment) => () => {
        setFocusedSegment(segment);
        if (typeof props.OnFocus === "function") {
            props.OnFocus();
        }
    };

    const padOnBlur = (segment) => {
        const hVal = hoursRef.current?.value ?? "";
        const mVal = minutesRef.current?.value ?? "";
        if (segment === "hours" && hVal.length === 1) {
            const padded = pad2(hVal);
            setHoursStr(padded);
            emit(padded, mVal, periodStr);
        }
        if (segment === "minutes" && mVal.length === 1) {
            const padded = pad2(mVal);
            setMinutesStr(padded);
            emit(hVal, padded, periodStr);
        }
    };

    const handleSegmentBlur = (segment) => (e) => {
        padOnBlur(segment);

        const related = e?.relatedTarget;
        const stayingInside = related && containerRef.current && containerRef.current.contains(related);
        if (stayingInside) {
            return;
        }
        setFocusedSegment(null);
        if (typeof props.OnBlur === "function") {
            // Compute from DOM values since setState is async.
            const hVal = hoursRef.current?.value ?? "";
            const mVal = minutesRef.current?.value ?? "";
            const hOut = hVal.length === 1 ? pad2(hVal) : hVal;
            const mOut = mVal.length === 1 ? pad2(mVal) : mVal;
            props.OnBlur(buildOutput(hOut, mOut, periodStr, usesAmPm));
        }
    };

    const copyToClipboard = async () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 5000);
        if (typeof navigator !== "undefined" && navigator.clipboard) {
            await navigator.clipboard.writeText(props.Value ?? "");
        }
    };

    const getClipboardIcon = () => copied ? faClipboardCheck : faClipboard;

    const setContainerRef = (node) => {
        containerRef.current = node;
        if (triggerRef) {
            triggerRef.current = node;
        }
    };

    const getContainerClass = () => {
        const focusClass = focusedSegment ? "eone-ui-input-container-focused " : "";
        const classDetails = getInputClassDetails(props.Value, props.ErrorMessage);
        if (props.ContainerClass) {
            return `${props.ContainerClass} ${classDetails}`;
        }
        return `eone-ui-input-container ${focusClass}${classDetails}`;
    };

    const pickerHourOptions = useMemo(() => {
        if (usesAmPm) {
            return Array.from({ length: 12 }, (_, i) => {
                const h = i + 1;
                return { value: h, label: pad2(h) };
            });
        }
        return Array.from({ length: 24 }, (_, i) => ({ value: i, label: pad2(i) }));
    }, [usesAmPm]);

    const pickerMinuteOptions = useMemo(() => {
        const count = Math.floor(60 / minuteStep);
        return Array.from({ length: count }, (_, i) => {
            const m = i * minuteStep;
            return { value: m, label: pad2(m) };
        });
    }, [minuteStep]);

    const pickerSelectedHour = hoursStr === "" ? null : parseInt(hoursStr, 10);
    const pickerSelectedMinute = minutesStr === "" ? null : parseInt(minutesStr, 10);

    const pickerSelectHour = (hour) => {
        const hours = pad2(hour);
        const minutes = minutesStr === "" ? "00" : minutesStr;
        setHoursStr(hours);
        setMinutesStr(minutes);
        emit(hours, minutes, periodStr);
        hoursRef.current?.focus();
    };

    const pickerSelectMinute = (minute) => {
        const hours = hoursStr === "" ? (usesAmPm ? "12" : "00") : hoursStr;
        const minutes = pad2(minute);
        setHoursStr(hours);
        setMinutesStr(minutes);
        emit(hours, minutes, periodStr);
    };

    const pickerSelectPeriod = (p) => setPeriod(p);

    const handleFieldsClick = (e) => {
        if (e.target === e.currentTarget) {
            hoursRef.current?.focus();
            hoursRef.current?.select?.();
        }
    };

    if (props.Visible !== undefined && !props.Visible) {
        return null;
    }

    const disabled = !!props.Disabled;

    return (
        <div className={getContainerClass()} ref={setContainerRef} data-testid={props.DataTestId}>
            <InputIcon Icon={props.Icon} DataTestId={props.DataTestId} />
            <div className="eone-ui-input-time-fields" onClick={handleFieldsClick}>
                <input
                    ref={hoursRef}
                    type="text"
                    inputMode="numeric"
                    className="eone-ui-input-time-segment"
                    id={props.Id}
                    value={hoursStr}
                    placeholder={usesAmPm ? "hh" : "HH"}
                    disabled={disabled}
                    autoFocus={props.AutoFocus}
                    aria-label="Hours"
                    maxLength={2}
                    size={2}
                    onChange={handleHoursChange}
                    onFocus={handleSegmentFocus("hours")}
                    onBlur={handleSegmentBlur("hours")}
                    onKeyDown={handleHoursKeyDown}
                    onPaste={handlePaste}
                    onClick={(e) => e.target.select()}
                    data-testid={getDataTestId(props.DataTestId, "hours")} />
                <span className="eone-ui-input-time-separator">:</span>
                <input
                    ref={minutesRef}
                    type="text"
                    inputMode="numeric"
                    className="eone-ui-input-time-segment"
                    value={minutesStr}
                    placeholder="mm"
                    disabled={disabled}
                    aria-label="Minutes"
                    maxLength={2}
                    size={2}
                    onChange={handleMinutesChange}
                    onFocus={handleSegmentFocus("minutes")}
                    onBlur={handleSegmentBlur("minutes")}
                    onKeyDown={handleMinutesKeyDown}
                    onPaste={handlePaste}
                    onClick={(e) => e.target.select()}
                    data-testid={getDataTestId(props.DataTestId, "minutes")} />
                {usesAmPm && (
                    <button
                        ref={periodRef}
                        type="button"
                        className="eone-ui-input-time-period"
                        onClick={togglePeriod}
                        onFocus={handleSegmentFocus("period")}
                        onBlur={handleSegmentBlur("period")}
                        onKeyDown={handlePeriodKeyDown}
                        disabled={disabled}
                        aria-label="AM or PM"
                        data-testid={getDataTestId(props.DataTestId, "period")}>
                        {periodStr}
                    </button>
                )}
            </div>
            {showPicker && !disabled && (
                <button
                    type="button"
                    className="eone-ui-input-time-picker-button"
                    onClick={toggleOpen}
                    aria-label="Open time picker"
                    aria-expanded={open}
                    data-testid={getDataTestId(props.DataTestId, "picker-button")}>
                    <FontAwesomeIcon fixedWidth={true} icon={faClock} />
                </button>
            )}
            <InputCopyButton
                Show={props.ShowCopyButton}
                Icon={getClipboardIcon()}
                Copy={copyToClipboard}
                DataTestId={props.DataTestId} />
            <InputErrorIndicator
                Error={props.ErrorMessage}
                DataTestId={props.DataTestId} />
            {showPicker && (
                <DropdownPanel Style={style} Open={open} Close={close} DropdownPanelRef={dropdownPanelRef}>
                    <div className="eone-ui-input-time-picker" data-testid={getDataTestId(props.DataTestId, "picker")}>
                        <TimePickerColumn
                            Open={open}
                            Options={pickerHourOptions}
                            Selected={pickerSelectedHour}
                            OnSelect={pickerSelectHour}
                            DataTestId={getDataTestId(props.DataTestId, "picker", "hour")} />
                        <TimePickerColumn
                            Open={open}
                            Options={pickerMinuteOptions}
                            Selected={pickerSelectedMinute}
                            OnSelect={pickerSelectMinute}
                            DataTestId={getDataTestId(props.DataTestId, "picker", "minute")} />
                        {usesAmPm && (
                            <TimePickerColumn
                                Open={open}
                                Options={[{ value: "AM", label: "AM" }, { value: "PM", label: "PM" }]}
                                Selected={periodStr}
                                OnSelect={pickerSelectPeriod}
                                DataTestId={getDataTestId(props.DataTestId, "picker", "period")} />
                        )}
                    </div>
                </DropdownPanel>
            )}
        </div>
    );
}

export { InputTime };
